import React, { useEffect, useState, useCallback } from 'react';
import {
  getOnboardingClients, createOnboardingClient, updateClientFields,
  setOnboardingStatus, inviteClientUser, listClientFiles, uploadClientFile, clientFileUrl, deleteClientFile, saveClientContract,
  type OnboardingClient, type ClientFile,
} from '../../lib/refonteApi';
import { SkeletonRows, Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

// Offres → couple (type, billing_unit) du dictionnaire cerveau (§7).
// Prix/mois partout. `hasDays` = on précise aussi un nb de jours (cold call) ; `hasRdv` = prix/RDV (hybride).
const OFFERS = [
  { key: 'cold_call_forfait', label: 'Cold call — forfait', type: 'cold_calling', pricing: 'fixed' as const, channel: 'cold_call', hasDays: true, hasRdv: false },
  { key: 'cold_call_hybride', label: 'Cold call — hybride', type: 'cold_calling', pricing: 'fixed_plus_variable' as const, channel: 'cold_call', hasDays: true, hasRdv: true },
  { key: 'gtm', label: 'GTM (multicanal)', type: 'gtm', pricing: 'gtm' as const, channel: 'gtm', hasDays: false, hasRdv: false },
  { key: 'mailing', label: 'Cold mailing', type: 'mailing', pricing: 'fixed' as const, channel: 'mailing', hasDays: false, hasRdv: false },
];

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block"><span className="font-mono text-[9px] uppercase tracking-widest text-faint">{label}</span>{children}</label>
);
const inputCls = 'mt-0.5 w-full rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px]';

// Onboarding client (CSM) — de la signature au lancement. Crée le client, pilote la checklist
// bloquante du cerveau (Sales Book/calibration, contrat, facturation, angle, encaissement, SDR),
// invite le compte client, puis lance la mission (onboarding_status = validated).
const OnboardingPage: React.FC = () => {
  const [clients, setClients] = useState<OnboardingClient[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Création
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', company_name: '', email: '', phone: '', contact_name: '', sector: '', calibrage_date: '' });

  // Éditeurs du client sélectionné
  const [offer, setOffer] = useState('cold_call_forfait');
  const [monthly, setMonthly] = useState('');
  const [pricePerRdv, setPricePerRdv] = useState('');
  const [dpm, setDpm] = useState('');
  const [startDate, setStartDate] = useState(''); // début du contrat (signé mi-sept → démarre 01/10)
  const [nbMonths, setNbMonths] = useState('');   // durée en mois → fin calculée (BP + registre suivent)
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [contactPhone, setContactPhone] = useState(''); // tél de la personne rattachée au compte
  const [showAll, setShowAll] = useState(false);
  const [leexi, setLeexi] = useState('');
  const [files, setFiles] = useState<ClientFile[]>([]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setClients(await getOnboardingClients()); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const client = clients.find(c => c.id === sel) || null;
  useEffect(() => {
    if (!client) return;
    setMonthly(client.price_per_day && client.days_per_month ? String(client.price_per_day * client.days_per_month) : '');
    setPricePerRdv('');
    setDpm(client.days_per_month ? String(client.days_per_month) : '');
    setContact(client.contact_name || '');
    setEmail(client.email || '');
    setContactPhone((client as any).phone || '');
    setLeexi((client as any).calibrage_video_url || '');
    listClientFiles(client.id).then(setFiles).catch(() => setFiles([]));
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  const run = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); flash(ok); }
    catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  const doCreate = () => run(async () => {
    if (!form.name.trim()) throw new Error('Le nom est requis');
    const id = await createOnboardingClient(form);
    if (form.calibrage_date) await updateClientFields(id, { calibrage_date: form.calibrage_date });
    setCreating(false); setForm({ name: '', company_name: '', email: '', phone: '', contact_name: '', sector: '', calibrage_date: '' });
    setSel(id);
  }, 'Client créé');
  const curOffer = OFFERS.find(o => o.key === offer) || OFFERS[0];
  const saveContract = () => run(async () => {
    // Le contrat en cours est MIS À JOUR (plus recréé) ; un nouveau contrat exige sa date de début.
    if (!startDate && !client!.hasContract) throw new Error('La date de début est obligatoire pour un nouveau contrat.');
    await saveClientContract(client!.id, {
      pricing: curOffer.pricing, type: curOffer.type, channel: curOffer.channel,
      monthly: Number(monthly) || 0,
      days: curOffer.hasDays ? (Number(dpm) || null) : null,
      pricePerRdv: curOffer.hasRdv ? (Number(pricePerRdv) || null) : null,
      startDate: startDate || null, months: nbMonths ? Number(nbMonths) : null,
    });
  }, 'Contrat enregistré (BP mis à jour)');
  const saveFactu = () => run(() => updateClientFields(client!.id, { contact_name: contact || null, email: email || null, phone: contactPhone || null }), 'Contact enregistré');
  const invite = () => run(() => inviteClientUser(email.trim(), client!.id, contact.trim() || undefined, undefined, contactPhone.trim() || undefined), 'Invitation envoyée');
  const saveLeexi = () => run(() => updateClientFields(client!.id, { calibrage_video_url: leexi.trim() || null }), 'Lien Leexi enregistré');
  const onUpload = (file?: File) => { if (!file || !client) return; run(async () => { await uploadClientFile(client.id, file); setFiles(await listClientFiles(client.id)); }, 'Fichier ajouté'); };
  const removeFile = (name: string) => { if (!client) return; run(async () => { await deleteClientFile(client.id, name); setFiles(await listClientFiles(client.id)); }, 'Fichier supprimé'); };
  const openFile = async (name: string) => { if (!client) return; const url = await clientFileUrl(client.id, name); if (url) window.open(url, '_blank'); };

  // Vérifications LUES EN BASE (plus de cases cochées à la main qui revenaient à faux à chaque ouverture).
  // Bloquants dès maintenant : contrat actif + contact facturation. Les trois autres deviennent bloquants
  // avec le parcours en 5 étapes (semaine 3 du plan du 03/11).
  const checksOf = (c: OnboardingClient) => [
    { k: 'contrat', l: 'Contrat actif', ok: c.hasContract, block: true, hint: 'Poser le contrat (bloc 1)' },
    { k: 'contact', l: 'Contact facturation (nom + mail)', ok: !!(c.contact_name && c.email), block: true, hint: 'Renseigner le contact (bloc 1)' },
    { k: 'calibrage', l: 'Calibrage en base', ok: c.calibrageInBase, block: false, hint: 'Envoyer le calibrage Leexi depuis #csm' },
    { k: 'sdr', l: 'SDR affecté (journées dans l\'agenda)', ok: c.sdrAssigned, block: false, hint: 'Planifier dans l\'Agenda' },
    { k: 'portail', l: 'Compte portail client', ok: c.portalAccount, block: false, hint: 'Inviter le client (bloc 3)' },
  ];
  const checks = client ? checksOf(client) : [];
  const blockers = checks.filter(x => x.block && !x.ok);
  const launched = client?.onboarding_status === 'validated';

  // Par défaut : seulement les clients à onboarder (non lancés, non terminés), les plus récents d'abord.
  const toOnboard = (c: OnboardingClient) => c.onboarding_status !== 'validated' && c.statut !== 'termine';
  const filtered = clients.filter(c => (showAll || toOnboard(c)) && (!q || `${c.name} ${c.company_name ?? ''}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Onboarding client</h1>
        <button onClick={() => { setCreating(true); setSel(null); }} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white hover:opacity-90">➕ Nouveau client</button>
      </div>
      <p className="text-dim text-[13px] mb-4">Les clients à onboarder. Créer, poser le contrat et le contact, déposer les sources, inviter, lancer quand le contrat et le contact sont là.</p>

      {err && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2">{err}</div>}
      {msg && <div className="mb-3 text-[12px] text-st-ok bg-st-ok/10 border border-st-ok/20 rounded-lg px-3 py-2">{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-start">
        {/* Liste clients */}
        <div className="bg-panel border border-line rounded-card p-4">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…" className={inputCls + ' mb-2'} />
          <label className="flex items-center gap-2 text-[11.5px] text-dim mb-3"><input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />Voir aussi les clients lancés et terminés <span className="ml-auto font-mono text-faint">{filtered.length}</span></label>
          {loading ? <SkeletonRows count={6} /> : (
            <div className="divide-y divide-line max-h-[70vh] overflow-y-auto -mx-1 px-1">
              {filtered.map(c => (
                <button key={c.id} onClick={() => { setSel(c.id); setCreating(false); }}
                  className={`w-full text-left py-2 px-1 flex items-center gap-2 rounded-lg ${sel === c.id ? 'bg-panel-2' : 'hover:bg-panel-2'}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold truncate">{c.name}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-faint">{c.onboarding_status === 'validated' ? '✓ lancé' : `${checksOf(c).filter(x => x.ok).length}/5 · ${c.hasContract ? 'contrat ok' : 'sans contrat'}`}</div>
                  </div>
                  {c.onboarding_status === 'validated' && <span className="w-2 h-2 rounded-full bg-st-ok shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Panneau onboarding */}
        <div>
          {creating ? (
            <div className="bg-panel border border-line rounded-card p-5 max-w-[520px]">
              <h2 className="text-[15px] font-bold mb-3">Nouveau client</h2>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nom *"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
                <Field label="Raison sociale"><input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className={inputCls} /></Field>
                <Field label="Contact"><input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className={inputCls} /></Field>
                <Field label="Secteur"><input value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })} className={inputCls} /></Field>
                <Field label="Email"><input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
                <Field label="Téléphone"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
                <Field label="Date de calibrage"><input type="date" value={form.calibrage_date} onChange={e => setForm({ ...form, calibrage_date: e.target.value })} className={inputCls} /></Field>
              </div>
              <div className="flex gap-2 mt-4">
                <button disabled={busy} onClick={doCreate} className="text-[13px] font-semibold px-4 py-2 rounded-md bg-hook-rose text-white disabled:opacity-40">Créer</button>
                <button onClick={() => setCreating(false)} className="text-[13px] px-4 py-2 rounded-md border border-line-2">Annuler</button>
              </div>
            </div>
          ) : loading ? <Skeleton className="h-[60vh] rounded-card" /> : !client ? (
            <EmptyState title="Choisis un client" hint="Sélectionne un client à onboarder, ou crée-en un nouveau." />
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-[17px] font-bold">{client.name}</h2>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-faint">{launched ? '✓ Mission lancée' : blockers.length ? `bloqué : ${blockers.map(b => b.l.toLowerCase()).join(', ')}` : 'prêt à lancer'}</div>
                </div>
                <button disabled={busy || launched || blockers.length > 0} title={blockers.length ? blockers.map(b => b.hint).join(' · ') : undefined}
                  onClick={() => run(() => setOnboardingStatus(client.id, 'validated'), 'Mission lancée 🚀')}
                  className={`text-[13px] font-semibold px-4 py-2 rounded-md ${!launched && !blockers.length ? 'bg-st-ok text-white hover:opacity-90' : 'bg-raise text-faint cursor-not-allowed'}`}>
                  {launched ? '✓ Lancée' : '🚀 Lancer la mission'}
                </button>
              </div>

              {/* Où en est ce client : 5 vérifications lues en base */}
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-faint">État de l'onboarding</span>
                  <span className="font-mono text-[11px] font-bold">{checks.filter(x => x.ok).length}/5</span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {checks.map(x => (
                    <li key={x.k} className="flex items-center gap-2 text-[12.5px]">
                      <span className={`w-4 text-center font-bold ${x.ok ? 'text-st-ok' : x.block ? 'text-st-bad' : 'text-st-warn'}`}>{x.ok ? '✓' : x.block ? '✕' : '!'}</span>
                      <span className={x.ok ? 'text-dim' : ''}>{x.l}</span>
                      {!x.ok && <span className="font-mono text-[10px] text-faint">· {x.hint}</span>}
                    </li>
                  ))}
                </ul>
                {!launched && <p className="text-[10.5px] text-faint mt-2">✕ bloque le lancement. ! est à faire avant la première journée.</p>}
              </div>

              {/* Bloc 1 — Contact & contrat */}
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-2"><span className="text-hook-rose font-bold">1</span> · Contact & contrat</div>
                <div className="flex flex-wrap items-end gap-2 mb-2">
                  <Field label="Contact"><input value={contact} onChange={e => setContact(e.target.value)} className={inputCls + ' w-44'} /></Field>
                  <Field label="Email"><input value={email} onChange={e => setEmail(e.target.value)} className={inputCls + ' w-52'} /></Field>
                  <Field label="Téléphone"><input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="06…" className={inputCls + ' w-40'} /></Field>
                  <button disabled={busy} onClick={saveFactu} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-line-2 hover:border-ink">Enregistrer</button>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Formule"><select value={offer} onChange={e => setOffer(e.target.value)} className={inputCls + ' w-48'}>{OFFERS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}</select></Field>
                  <Field label="Prix/mois €"><input value={monthly} onChange={e => setMonthly(e.target.value)} placeholder="2600" className={inputCls + ' w-24'} /></Field>
                  {curOffer.hasDays && <Field label="Jours/mois"><input value={dpm} onChange={e => setDpm(e.target.value)} className={inputCls + ' w-20'} /></Field>}
                  {curOffer.hasRdv && <Field label="Prix/RDV €"><input value={pricePerRdv} onChange={e => setPricePerRdv(e.target.value)} placeholder="200" className={inputCls + ' w-20'} /></Field>}
                  <Field label="Début"><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls + ' w-36'} /></Field>
                  <Field label="Durée (mois)"><input value={nbMonths} onChange={e => setNbMonths(e.target.value)} placeholder="3" className={inputCls + ' w-20'} /></Field>
                  <button disabled={busy} onClick={saveContract} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-line-2 hover:border-ink">Enregistrer le contrat</button>
                </div>
              </div>

              {/* Bloc 2 — Sources : Leexi + fichiers */}
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-2"><span className="text-hook-rose font-bold">2</span> · Sources — calibrage (Leexi) & fichiers</div>
                <div className="flex flex-wrap items-end gap-2 mb-3">
                  <Field label="Lien Leexi (transcript visio)"><input value={leexi} onChange={e => setLeexi(e.target.value)} placeholder="https://app.leexi.ai/…" className={inputCls + ' w-72'} /></Field>
                  <button disabled={busy} onClick={saveLeexi} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-line-2 hover:border-ink">Enregistrer</button>
                  {leexi && /^https?:/.test(leexi) && <a href={leexi} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-hook-rose hover:underline">Ouvrir ↗</a>}
                </div>
                <label className="inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-line-2 hover:border-ink cursor-pointer">
                  📎 Ajouter un fichier<input type="file" className="hidden" onChange={e => onUpload(e.target.files?.[0])} disabled={busy} />
                </label>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map(fl => (
                      <li key={fl.name} className="flex items-center gap-2 text-[12.5px]">
                        <button onClick={() => openFile(fl.name)} className="text-hook-rose hover:underline text-left flex-1 truncate">📄 {fl.name.replace(/^\d+_/, '')}</button>
                        <button onClick={() => removeFile(fl.name)} className="font-mono text-[10px] text-st-bad hover:underline">suppr.</button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10.5px] text-faint mt-2">Le calibrage arrive tout seul depuis Leexi quand le titre de la réunion contient « Calibrage » : on le relit et on l'envoie depuis #csm. Ici : le lien de la visio et les fichiers du client (brief, listes, exclusions).</p>
              </div>

              {/* Bloc 3 — Compte client */}
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-2"><span className="text-hook-rose font-bold">3</span> · Compte client</div>
                <div className="flex items-end gap-2 flex-wrap">
                  <Field label="Email du client"><input value={email} onChange={e => setEmail(e.target.value)} className={inputCls + ' w-56'} /></Field>
                  <Field label="Téléphone"><input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="06…" className={inputCls + ' w-40'} /></Field>
                  <button disabled={busy || !email.trim()} onClick={invite} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-line-2 hover:border-ink disabled:opacity-40">✉️ Inviter le client</button>
                </div>
                <p className="text-[10.5px] text-faint mt-2">Le nom (contact) et le téléphone saisis ci-dessus sont rattachés au compte à l'invitation.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
